import { useState, useCallback, useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { EVENTS } from '@lib/analytics';
import { Modal } from 'antd';
import LndButton from '@components/LndButton';
import ShimmerText from '@components/ShimmerText';
import AppLogo, { Theme } from '@components/AppLogo';
import { useObjectives, useCreateObjective } from '@apis/queries/useObjectives';
import { useQueryClient } from '@tanstack/react-query';
import { feedQueryKeys } from '@apis/queries/useFeed';
import { objectiveFeedQueryKeys } from '@apis/queries/useObjectiveFeed';
import ObjectiveService from '@apis/ObjectiveService';
import type { ObjectiveSuggestion } from '@apis/ObjectiveService';

// Cap how long we'll wait for the backend's tailored suggestions before
// falling back to a generic objective. The previous experience polled for a
// company-summary signal but we don't show that signal anymore — a hard
// ceiling keeps the user from staring at the shimmer indefinitely.
const SUGGESTION_TIMEOUT_MS = 30000;

const FALLBACK_SUGGESTION: ObjectiveSuggestion = {
  title: 'Increase Sales & Revenue from Ads',
  description: 'Drive direct product or service sales through advertising campaigns',
};

interface WelcomeModalProps {
  onClose: () => void;
  hideContent?: boolean;
  source?: 'onboarding_redirect';
}

/**
 * Welcome modal shown after workspace onboarding completes. Auto-creates a
 * starter objective so the user lands on a populated feed; the modal blocks
 * dismissal with a shimmer text indicator until that backend work settles.
 * Only entered from the signup wizard — Leo owns objective creation elsewhere.
 */
export default function WelcomeModal({ onClose, source }: WelcomeModalProps) {
  const queryClient = useQueryClient();
  const createObjective = useCreateObjective();
  const { objectives: existingObjectives, isLoading: isLoadingObjectives } = useObjectives();

  // `isReady` flips when the backend work (suggestion fetch + objective create
  // + feed/objective-feed invalidation) is done. While false, the modal shows
  // shimmer text and hides the Continue button.
  const [isReady, setIsReady] = useState(false);

  const hasStartedRef = useRef(false);
  const hasSavedRef = useRef(false);
  const hasTrackedViewRef = useRef(false);

  // Track modal view once
  useEffect(() => {
    if (hasTrackedViewRef.current || isLoadingObjectives) return;
    hasTrackedViewRef.current = true;
    posthog.capture(EVENTS.OBJECTIVES.MODAL_VIEWED, {
      source: source ?? 'onboarding_redirect',
      existing_objective_count: existingObjectives.length,
    });
  }, [isLoadingObjectives, existingObjectives.length, source]);

  // Track dismiss on unmount when nothing was saved (so we don't double-count
  // a normal close as a dismiss).
  useEffect(() => {
    return () => {
      if (hasSavedRef.current) return;
      posthog.capture(EVENTS.OBJECTIVES.MODAL_DISMISSED, {
        objectives_selected_at_dismiss: 0,
        had_suggestions: false,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire-once on unmount
  }, []);

  // Auto-create the starter objective. Runs once per mount, after objective
  // existence is known. If the workspace already has objectives, skip the
  // create and flip ready immediately.
  useEffect(() => {
    if (hasStartedRef.current || isLoadingObjectives) return;
    hasStartedRef.current = true;

    if (existingObjectives.length > 0) {
      setIsReady(true);
      return;
    }

    const run = async () => {
      let recommended: ObjectiveSuggestion = FALLBACK_SUGGESTION;
      try {
        const suggestions = await Promise.race<ObjectiveSuggestion[]>([
          ObjectiveService.getSuggestions(1),
          new Promise<ObjectiveSuggestion[]>((_, reject) =>
            setTimeout(() => reject(new Error('suggestion timeout')), SUGGESTION_TIMEOUT_MS),
          ),
        ]);
        if (suggestions.length > 0) {
          recommended = suggestions.find((s) => !!s.badge) ?? suggestions[0];
        }
      } catch (err) {
        console.warn('[WelcomeModal] suggestion fetch failed, using fallback:', err);
      }

      try {
        await createObjective.mutateAsync({
          title: recommended.title,
          description: recommended.description,
          progress_detail: recommended.progress_detail,
          discovery_questions: recommended.discovery_questions,
          kickoff_body: recommended.kickoff_body,
          skill_insights: recommended.skill_insights,
        });
        await ObjectiveService.classifyPosts();
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: feedQueryKeys.all }),
          queryClient.invalidateQueries({ queryKey: objectiveFeedQueryKeys.all }),
        ]);
        hasSavedRef.current = true;
        posthog.capture(EVENTS.OBJECTIVES.SAVED, {
          total_objectives: 1,
          suggested_objectives: 1,
          custom_objectives: 0,
          existing_objectives_kept: 0,
          objectives_removed: 0,
          suggestion_titles: [recommended.title],
        });
      } catch (err) {
        console.error('[WelcomeModal] objective create failed:', err);
      }

      setIsReady(true);
    };

    run();
  }, [isLoadingObjectives, existingObjectives.length, createObjective, queryClient]);

  const handleClose = useCallback(async () => {
    // requestAnimationFrame gives the button a chance to render its
    // pressed state before the modal unmounts.
    await new Promise((r) => requestAnimationFrame(r));
    onClose();
  }, [onClose]);

  return (
    <Modal
      open
      closable={false}
      footer={null}
      width={460}
      centered
      maskClosable={false}
      focusable={{ trap: true }}
      rootClassName="welcome-modal"
      styles={{ body: { padding: 0 } }}
    >
      <div className="flex flex-col items-center text-center gap-6 px-10 py-12">
        <AppLogo compact size={48} displayLeoLogo theme={Theme.DARK} />
        <h2 className="text-2xl font-semibold text-body m-0">Welcome to Leo</h2>
        <div className="text-sm leading-relaxed min-h-[48px] flex items-center justify-center">
          {isReady ? (
            <span className="text-leo-secondary max-w-[380px]">
              Your workspace is ready. Leo is reviewing your account in the
              background — open the feed to see what comes back.
            </span>
          ) : (
            <ShimmerText durationSeconds={2.5}>
              Getting your workspace set up…
            </ShimmerText>
          )}
        </div>
        <LndButton
          variant="primary"
          size="large"
          onClick={handleClose}
          disabled={!isReady}
          data-attr-welcome-modal-continue
        >
          Continue
        </LndButton>
      </div>
    </Modal>
  );
}
